// Version: 2.7
// v2.7: Phone width and the light page. The Pipeline title and subtitle were drawn in
//       near-white on the grey page, so the title could not be seen at any width. On a
//       phone the Enrich menu opens to the right of its button (it opened off the left
//       edge of the screen), and the page leaves room under the last cards for the
//       Select bar, which covered them. Stage chips above the board (Board.js v1.4).
// v2.6: Dead lead bucket, pipeline rules and bulk changes (CV-54, app.py
//       v4.37). Dead is a closed column shown with Won and Lost; a Dead card
//       says which stage it went quiet in and Revive puts it back there.
//       Select mode: tick cards (or a whole column) and use the bar at the
//       bottom to add or remove tags, move stage, set the owner, revive or
//       delete them in one go. Rules button (sales.rules) opens the rule
//       builder and run log (SalesRules.js). Lead drawer: They replied button
//       and Reply activity type (recorded as a response, which stops "no
//       response" timers and revives Dead leads through the starter rule),
//       last response time, and rule entries in the activity feed.
// v2.5: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size. Panels still drawn dark carry className="surface-dark" so their buttons pick up dark values.
// v2.4: Converting a lead says the client's name, not its folder name.
// v2.3: Internet exposure shows known CVEs and which source answered (Shodan or
//       the free InternetDB fallback).
// v2.2: Domain suggestion chips have a dismiss button. A dismissed domain is
//       removed from the lead and not suggested again by Enrich.
// v2.1: Widened the add-tag follow-up field so its placeholder isn't clipped.
// v2.0: Tags and deeper enrich. Leads carry coloured tags (Trello-style, many
//       per card) with an optional follow-up timer; a tag with a timer shows a
//       countdown fill and, when it is the earliest due, drives the lead's next
//       action. Tags are managed in a Tags modal and filtered on the board.
//       Full enrich button gathers breach exposure, subdomains and the site's
//       internet-facing services. Buttons respect the signed-in user's sales
//       permissions (meta.permissions).
// v1.9: Lead assessment wording uses the agreed name, External Cyber
//        Security Assessment.
// 1.8: Company name is editable (pencil next to it in the drawer). Company
//      section has a Companies House number field: saving it looks the
//      company up and fills in its official name, address, incorporation
//      date and SIC codes (app.py v3.88). When Enrich finds several possible
//      companies by name, they are listed with a Use button for each.
// 1.7: Domain suggestions that came from the web search are tagged "search",
//      and What Enrich found has a Web search line saying how many results it
//      returned, that they came from the 7-day cache, or why it didn't run
//      (app.py v3.87).
// 1.6: Enrichment v2 (app.py v3.86). Lead drawer shows how the website was
//      found or confirmed, company size and health (accounts category,
//      overdue filings, insolvency), owners (PSC), website details (phones,
//      published emails, social pages, WordPress, certificate expiry), DKIM and
//      MTA-STS, and why the lead scored as it did. Enrich now also offers
//      director email guesses from the saved company pattern. Pipeline header
//      has an Enrich menu (all New, not yet enriched, older than 30 days) that
//      runs in the background with a progress bar. New contact sources:
//      Website and Owner.
// 1.5: Add to Client Management form starts from GET .../convert-preview
//      (app.py v3.85): suggested company code not used by any other client
//      (editable, blank picks one automatically), phone, and a list of what
//      else comes across (address, sectors, contacts, account manager,
//      assessment reports and so on).
// 1.4: "Convert to client" renamed "Add to Client Management" (button and
//      dialog). The dialog says the lead's assessment reports carry across
//      when it has been assessed (app.py v3.84 makes that work).
// 1.3: Share assessment report (B1) — "Share" on a report mints a public tracked
//      link; shared links show open counts and last-opened on the lead. Backend
//      app.py v3.66.
// 1.2: Prospect score badge on cards and detail (A1), email-security posture chips
//      (DMARC/SPF/mail provider), and outreach engagement (sent/opened/clicked)
//      with email_open/email_click in the activity feed (A3). Backend app.py v3.65.
// 1.1: Run assessment POST now sends an empty JSON body so Flask does not
//      reject it with 415 Unsupported Media Type (the endpoint reads
//      request.get_json()).
// 1.0: Sales pipeline board. Stages New, Researching, Contacted, Engaged,
//      Proposal, Won and Lost as columns (Board.js). Drag a card to change
//      stage; moving to Lost asks for a reason. Won and Lost can be hidden.
//      Lead drawer: details, owner, value, next action, domain (possible
//      matches from Enrich are labelled as such until confirmed), notes,
//      contacts (Companies House officers, Hunter, manual), outreach email
//      through the Prospect Outreach template with suppression checks,
//      activity timeline, and convert to client.
//      Opens a lead directly from /sales/pipeline?lead=ID.
//      Run an external assessment on a lead natively (POST .../assess);
//      progress and the finished report appear on the lead.
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Board from "./Board";
import SalesRules from "./SalesRules";
import {
  useIsMobile, api, fmtDate, fmtUpdated, ago, money, todayYmd, STAGE_META, Badge, Modal, ErrorBox, StatCard,
  pageStyle, labelStyle, inputStyle, btnSecondary, sectionTitle,
} from "./salesShared";

const SOURCE_META = {
  officer: { label: "Officer", color: "#bf5af2" },
  owner:   { label: "Owner",   color: "#ff375f" },
  website: { label: "Website", color: "#64d2ff" },
  hunter:  { label: "Hunter",  color: "#00d4ff" },
  guess:   { label: "Guess",   color: "#ff9f0a" },
  manual:  { label: "Manual",  color: "#30d158" },
};

const ACTIVITY_META = {
  note:         { icon: "ti-note",            label: "Note" },
  call:         { icon: "ti-phone",           label: "Call" },
  email:        { icon: "ti-mail",            label: "Email" },
  email_open:   { icon: "ti-mail-opened",     label: "Opened" },
  email_click:  { icon: "ti-click",           label: "Clicked" },
  meeting:      { icon: "ti-users",           label: "Meeting" },
  reply:        { icon: "ti-message-reply",   label: "Reply" },
  rule:         { icon: "ti-bolt",            label: "Rule" },
  stage_change: { icon: "ti-arrows-right",    label: "Stage" },
  system:       { icon: "ti-info-circle",     label: "System" },
};

// A1: prospect score bands. Higher score = hotter (weaker email security + good
// fit). Colour runs hot(red/orange) to cold(grey) deliberately — a "Hot" lead is
// the one worth calling first.
const SCORE_META = {
  Hot:  { color: "#ff453a", bg: "rgba(255,69,58,0.14)" },
  Warm: { color: "#ff9f0a", bg: "rgba(255,159,10,0.14)" },
  Cool: { color: "#64d2ff", bg: "rgba(100,210,255,0.14)" },
  Cold: { color: "#86868b", bg: "rgba(134,134,139,0.14)" },
};

function ScoreBadge({ score, band, size = "sm" }) {
  if (!band) return null;
  const m = SCORE_META[band] || SCORE_META.Cold;
  const fs = size === "lg" ? 12 : 10;
  return (
    <span title={`Prospect score ${score} of 100 (${band}) — higher means weaker email security and a better fit`}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: fs, fontWeight: 700,
        padding: size === "lg" ? "3px 9px" : "1px 7px", borderRadius: 5, color: m.color, background: m.bg,
        border: `1px solid ${m.color}40`, fontFamily: "'SF Mono',monospace", whiteSpace: "nowrap", flexShrink: 0 }}>
      <i className="ti ti-flame" style={{ fontSize: fs }} />{score}
    </span>
  );
}

// Email-security posture chips used on the lead detail.
function PostureChips({ posture, mxProvider }) {
  if (!posture || !Object.keys(posture).length) return null;
  const dmarc = posture.dmarc || "unknown";
  const spf   = posture.spf || "unknown";
  const dmarcColor = { reject: "#30d158", quarantine: "#e6b800", none: "#ff9f0a", missing: "#ff453a", unknown: "#86868b" }[dmarc] || "#86868b";
  const spfColor   = { present: "#30d158", missing: "#ff453a", unknown: "#86868b" }[spf] || "#86868b";
  const chip = (label, val, color) => (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 5, color, background: `${color}1f`, border: `1px solid ${color}40`, fontFamily: "'SF Mono',monospace" }}>
      {label}: {val}
    </span>
  );
  const prov = mxProvider || posture.mx_provider || "unknown";
  const provColor = prov === "Microsoft 365" ? "#0a84ff" : prov === "Google Workspace" ? "#30d158" : prov === "none" ? "#ff453a" : "#86868b";
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
      {chip("DMARC", dmarc, dmarcColor)}
      {chip("SPF", spf, spfColor)}
      {chip("Mail", prov, provColor)}
    </div>
  );
}

function hexA(hex, a) {
  const h = (hex || "#0a84ff").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map(c => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

// A tag label. When the tag has a follow-up timer, the label fills from left to
// right as the window elapses and turns amber then red as the due date nears or
// passes, so the countdown is visible at a glance.
function TagChip({ tag, onRemove }) {
  let fill = 0, urgent = 0;
  if (tag.follow_up_days && tag.applied_at && tag.due) {
    const start = new Date(tag.applied_at).getTime();
    const end = new Date(tag.due + "T00:00:00").getTime();
    const now = Date.now();
    fill = Math.max(0, Math.min(1, (now - start) / Math.max(1, end - start)));
    const daysLeft = Math.ceil((end - now) / 86400000);
    urgent = daysLeft <= 0 ? 2 : daysLeft <= 1 ? 1 : 0;
  }
  const base = tag.color || "#0a84ff";
  const edge = urgent === 2 ? "#ff453a" : urgent === 1 ? "#ff9f0a" : base;
  return (
    <span title={tag.due ? `Follow up by ${tag.due}` : tag.name}
      style={{ position: "relative", overflow: "hidden", display: "inline-flex", alignItems: "center", gap: 4,
        fontSize: 10, fontWeight: 700, color: edge, borderRadius: 5, padding: "2px 7px",
        border: `1px solid ${hexA(edge, 0.5)}`, background: hexA(base, 0.12), lineHeight: 1.4, maxWidth: "100%" }}>
      {tag.follow_up_days && tag.due && (
        <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${fill * 100}%`,
          background: hexA(edge, 0.22), pointerEvents: "none" }} />
      )}
      <span style={{ position: "relative", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tag.name}</span>
      {onRemove && <span onClick={e => { e.stopPropagation(); onRemove(tag); }} style={{ position: "relative", cursor: "pointer", opacity: 0.7, flexShrink: 0 }}>×</span>}
    </span>
  );
}

function TagList({ tags, onRemove, wrap = true }) {
  if (!tags || !tags.length) return null;
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: wrap ? "wrap" : "nowrap", overflow: wrap ? "visible" : "hidden" }}>
      {tags.map(t => <TagChip key={t.id} tag={t} onRemove={onRemove} />)}
    </div>
  );
}

// In-drawer editor: shows the lead's tags and a menu of the rest to add.
function TagEditor({ lead, allTags, canEdit, onChange }) {
  const [open, setOpen] = useState(false);
  const have = new Set((lead.tags || []).map(t => t.id));
  const available = (allTags || []).filter(t => !have.has(t.id));
  const set = (ids) => onChange(ids);
  return (
    <div>
      <div style={sectionTitle}>Tags</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <TagList tags={lead.tags} onRemove={canEdit ? (t => set((lead.tags || []).filter(x => x.id !== t.id).map(x => x.id))) : null} />
        {!((lead.tags || []).length) && <span style={{ fontSize: 12, color: "#86868b" }}>No tags yet.</span>}
        {canEdit && (
          <div style={{ position: "relative" }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setOpen(o => !o)}>+ Tag</button>
            {open && (
              <div className="surface-dark" style={{ position: "absolute", zIndex: 20, top: "110%", left: 0, minWidth: 180, background: "#1c1c1e",
                border: "1px solid #2c2c2e", borderRadius: 8, padding: 6, display: "flex", flexDirection: "column", gap: 2,
                boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}>
                {available.length === 0 && <div style={{ fontSize: 12, color: "#86868b", padding: "6px 8px" }}>All tags added. Make more in Tags.</div>}
                {available.map(t => (
                  <button className="btn btn-quiet" style={{ textAlign: "left" }} key={t.id} onClick={() => { set([...(lead.tags || []).map(x => x.id), t.id]); setOpen(false); }}
                   >
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: t.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "#f5f5f7" }}>{t.name}</span>
                    {t.follow_up_days ? <span style={{ fontSize: 10, color: "#86868b", marginLeft: "auto" }}>{t.follow_up_days}d</span> : null}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {(lead.tags || []).some(t => t.due) && (
        <div style={{ fontSize: 11, color: "#86868b", marginTop: 6 }}>
          Follow-up {(lead.tags || []).filter(t => t.due).length > 1 ? "dates" : "date"} from tags set the next action automatically unless you change it by hand.
        </div>
      )}
    </div>
  );
}

// Manage the tag catalogue: name, colour, optional follow-up days.
function TagManager({ tags, colours, onClose, onChanged }) {
  const [list, setList] = useState(tags);
  const [form, setForm] = useState({ name: "", color: (colours && colours[0]) || "#0a84ff", follow_up_days: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const refresh = (d) => { setList(d); onChanged(d); };
  const create = async () => {
    setBusy(true); setErr("");
    try {
      const d = await api("/api/sales/tags", { method: "POST", body: { name: form.name.trim(), color: form.color, follow_up_days: form.follow_up_days || null } });
      refresh(d); setForm({ name: "", color: form.color, follow_up_days: "" });
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const update = async (id, body) => { try { refresh(await api(`/api/sales/tags/${id}`, { method: "PUT", body })); } catch (e) { setErr(e.message); } };
  const remove = async (t) => {
    if (!window.confirm(`Delete the tag "${t.name}"?${t.leads ? ` It is on ${t.leads} lead${t.leads === 1 ? "" : "s"}.` : ""}`)) return;
    try { refresh(await api(`/api/sales/tags/${t.id}`, { method: "DELETE" })); } catch (e) { setErr(e.message); }
  };
  return (
    <Modal title="Tags" onClose={onClose} maxWidth={560}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <ErrorBox>{err}</ErrorBox>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {list.length === 0 && <div style={{ fontSize: 13, color: "#86868b" }}>No tags yet. Add one below — for example Brochure, Email or Facebook.</div>}
          {list.map(t => (
            <div className="surface-dark" key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", background: "#1c1c1e", border: "1px solid #2c2c2e", borderRadius: 8, padding: "8px 10px" }}>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {(colours || []).map(c => (
                  <span key={c} onClick={() => update(t.id, { color: c })} title={c}
                    style={{ width: 16, height: 16, borderRadius: 4, background: c, cursor: "pointer", border: t.color === c ? "2px solid #fff" : "2px solid transparent" }} />
                ))}
              </div>
              <input defaultValue={t.name} onBlur={e => e.target.value.trim() && e.target.value.trim() !== t.name && update(t.id, { name: e.target.value.trim() })}
                style={{ ...inputStyle, flex: "1 1 120px", minWidth: 0 }} />
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input type="number" min="0" max="365" defaultValue={t.follow_up_days || ""} placeholder="—"
                  onBlur={e => update(t.id, { follow_up_days: e.target.value ? Number(e.target.value) : null })}
                  style={{ ...inputStyle, width: 64 }} title="Follow-up after this many days (blank for none)" />
                <span style={{ fontSize: 11, color: "#86868b" }}>days</span>
              </div>
              <span style={{ fontSize: 11, color: "#86868b" }}>{t.leads || 0} lead{t.leads === 1 ? "" : "s"}</span>
              <button className="btn btn-secondary btn-sm" onClick={() => remove(t)}>Delete</button>
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid #2c2c2e", paddingTop: 12 }}>
          <div style={sectionTitle}>Add a tag</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 4 }}>
              {(colours || []).map(c => (
                <span key={c} onClick={() => setForm(f => ({ ...f, color: c }))} style={{ width: 18, height: 18, borderRadius: 4, background: c, cursor: "pointer", border: form.color === c ? "2px solid #fff" : "2px solid transparent" }} />
              ))}
            </div>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Tag name" style={{ ...inputStyle, flex: "1 1 120px", minWidth: 0 }} />
            <input type="number" min="0" max="365" value={form.follow_up_days} onChange={e => setForm(f => ({ ...f, follow_up_days: e.target.value }))} placeholder="Follow-up days" title="Follow-up after this many days (optional)" style={{ ...inputStyle, width: 140, flexShrink: 0 }} />
            <button className="btn btn-primary" disabled={busy || !form.name.trim()} onClick={create}>Add</button>
          </div>
          <div style={{ fontSize: 11, color: "#86868b", marginTop: 6 }}>A follow-up is optional. When set, applying the tag to a lead schedules its next action that many days later.</div>
        </div>
      </div>
    </Modal>
  );
}

export default function Pipeline({ user }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = user?.username || "";

  const [meta, setMeta] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [owner, setOwner] = useState("");
  const [showClosed, setShowClosed] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [lostPrompt, setLostPrompt] = useState(null);   // { lead, reason }
  const [showAdd, setShowAdd] = useState(false);
  const [showSuppression, setShowSuppression] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagFilter, setTagFilter] = useState([]);   // tag ids to filter by (AND)
  const [showTags, setShowTags] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [showBulkTags, setShowBulkTags] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkInfo, setBulkInfo] = useState("");
  const perms = new Set(meta?.permissions || []);
  const can = (p) => !meta || perms.has(p);   // before meta loads, assume yes (admin default)

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([api("/api/sales/leads"), api("/api/sales/stats")]);
      setLeads(l); setStats(s);
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => {
    api("/api/sales/meta").then(setMeta).catch(e => setError(e.message));
    api("/api/sales/tags").then(setTags).catch(() => {});
    load().finally(() => setLoading(false));
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const id = new URLSearchParams(location.search).get("lead");
    if (id) setOpenId(Number(id));
  }, [location.search]);

  const closeDrawer = () => {
    setOpenId(null);
    if (new URLSearchParams(location.search).get("lead")) navigate("/sales/pipeline", { replace: true });
  };

  const onLeadChanged = useCallback((lead) => {
    if (!lead) return;
    setLeads(prev => prev.some(l => l.id === lead.id)
      ? prev.map(l => l.id === lead.id ? { ...l, ...lead, contacts: undefined, activities: undefined } : l)
      : [lead, ...prev]);
    api("/api/sales/stats").then(setStats).catch(() => {});
  }, []);

  async function moveLead(lead, to, extra = {}) {
    const prevStage = lead.stage;
    setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, stage: to } : l));
    try {
      const updated = await api(`/api/sales/leads/${lead.id}`, { method: "PUT", body: { stage: to, ...extra } });
      onLeadChanged(updated);
    } catch (e) {
      setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, stage: prevStage } : l));
      setError(e.message);
    }
  }

  // ── Bulk changes (v2.6) ─────────────────────────────────────────────────────
  const selectedLeads = leads.filter(l => selected.has(l.id));
  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); setBulkInfo(""); };
  const toggleSelect = (l) => setSelected(prev => { const n = new Set(prev); n.has(l.id) ? n.delete(l.id) : n.add(l.id); return n; });
  const toggleColumn = (col, all) => setSelected(prev => {
    const n = new Set(prev);
    col.items.forEach(l => (all ? n.add(l.id) : n.delete(l.id)));
    return n;
  });

  async function bulk(body, done) {
    const ids = selectedLeads.map(l => l.id);
    if (!ids.length) return;
    setBulkBusy(true); setError(""); setBulkInfo("");
    try {
      const r = await api("/api/sales/leads/bulk", { method: "POST", body: { ids, ...body } });
      setBulkInfo(`${done}: ${r.changed} lead${r.changed === 1 ? "" : "s"} changed${r.skipped ? `, ${r.skipped} already that way` : ""}.`);
      if (body.action === "delete") setSelected(new Set());
      await load();
      api("/api/sales/tags").then(setTags).catch(() => {});
    } catch (e) { setError(e.message); }
    finally { setBulkBusy(false); }
  }

  function bulkStage(stage) {
    if (!stage) return;
    if (stage === "Lost") { setLostPrompt({ bulk: true, reason: "" }); return; }
    bulk({ action: "stage", stage }, `Moved to ${stage}`);
  }

  function bulkDelete() {
    const n = selectedLeads.length;
    if (!window.confirm(`Delete ${n} lead${n === 1 ? "" : "s"} and all their contacts and activity? This cannot be undone.`)) return;
    bulk({ action: "delete" }, "Deleted");
  }

  function handleMove(lead, from, to) {
    if (to === "Lost") { setLostPrompt({ lead, reason: lead.lost_reason || "" }); return; }
    moveLead(lead, to);
  }

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return leads.filter(l => {
      if (owner === "__none" ? l.owner : owner && l.owner !== owner) return false;
      if (s && !(`${l.company_name} ${l.company_number || ""} ${l.domain} ${l.postcode} ${(l.sectors || []).join(" ")}`.toLowerCase().includes(s))) return false;
      if (tagFilter.length) { const ids = new Set((l.tags || []).map(t => t.id)); if (!tagFilter.every(id => ids.has(id))) return false; }
      return true;
    });
  }, [leads, q, owner, tagFilter]);

  const stages = meta?.stages || ["New", "Researching", "Contacted", "Engaged", "Proposal", "Won", "Lost", "Dead"];
  const visibleStages = showClosed ? stages : stages.filter(s => !["Won", "Lost", "Dead"].includes(s));
  const today = todayYmd();

  const columns = visibleStages.map(stage => {
    const items = filtered.filter(l => l.stage === stage);
    const value = items.reduce((t, l) => t + (Number(l.value) || 0), 0);
    return { id: stage, title: stage, color: STAGE_META[stage]?.color, items, subtitle: value ? money(value) : "" };
  });

  function renderCard(l) {
    const overdue = l.next_action_date && l.next_action_date < today && !["Won", "Lost"].includes(l.stage);
    const dueToday = l.next_action_date === today;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "flex-start", justifyContent: "space-between" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#f5f5f7", lineHeight: 1.3, minWidth: 0 }}>{l.company_name}</div>
          {l.score_band && <ScoreBadge score={l.score} band={l.score_band} />}
        </div>
        {(l.sectors || []).length > 0 && (
          <div style={{ fontSize: 11, color: "#86868b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.sectors.join(", ")}</div>
        )}
        {l.domain && (
          <div style={{ fontSize: 11, color: l.domain_status === "possible" ? "#ff9f0a" : "#64d2ff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {l.domain}{l.domain_status === "possible" ? " (possible)" : ""}
          </div>
        )}
        {(l.tags || []).length > 0 && <TagList tags={l.tags} />}
        {l.next_action && (
          <div style={{ fontSize: 11, color: overdue ? "#ff453a" : dueToday ? "#ff9f0a" : "#d1d1d6", display: "flex", gap: 4, alignItems: "center", minWidth: 0 }}>
            <i className="ti ti-clock" style={{ flexShrink: 0 }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {l.next_action}{l.next_action_date ? ` · ${fmtDate(l.next_action_date)}` : ""}
            </span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, marginTop: 2 }}>
          <span style={{ fontSize: 11, color: "#86868b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {l.owner ? l.owner.split("@")[0] : "No owner"}{l.email_contacts ? ` · ${l.email_contacts} email${l.email_contacts === 1 ? "" : "s"}` : ""}
          </span>
          {Number(l.value) > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: "#30d158", flexShrink: 0 }}>{money(l.value)}</span>}
        </div>
        {l.converted_slug && <div><Badge label="Client" color="#30d158" bg="rgba(48,209,88,0.12)" /></div>}
        {l.stage === "Dead" && (
          <div style={{ fontSize: 11, color: "#8e8e93" }}>
            Went quiet{l.dead_from_stage ? ` in ${l.dead_from_stage}` : ""}{l.dead_at ? ` · ${ago(l.dead_at)}` : ""}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ ...pageStyle(isMobile), paddingBottom: selectMode ? (isMobile ? 190 : 110) : undefined }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <div>
          <h1 className="page-title" style={{ margin: "0 0 4px" }}>Pipeline</h1>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 13 }}>Prospects from first look to won or lost</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {can("sales.enrich") && <EnrichBatch onFinished={load} />}
          {can("sales.edit") && <button className="btn btn-secondary" aria-pressed={selectMode} onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}>{selectMode ? "Done selecting" : "Select"}</button>}
          {can("sales.tags") && <button className="btn btn-secondary" onClick={() => setShowTags(true)}>Tags</button>}
          {can("sales.rules") && <button className="btn btn-secondary" onClick={() => setShowRules(true)}>Rules</button>}
          {can("sales.edit") && <button className="btn btn-secondary" onClick={() => setShowSuppression(true)}>Suppression list</button>}
          {can("sales.search") && <button className="btn btn-secondary" onClick={() => navigate("/sales/search")}>Prospect Search</button>}
          {can("sales.search") && <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add lead</button>}
        </div>
      </div>

      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 16 }}>
          <StatCard label="Open leads" value={stats.open_count} />
          <StatCard label="Open value" value={money(stats.open_value)} color="#30d158" />
          <StatCard label="Overdue actions" value={stats.overdue} color={stats.overdue ? "#ff453a" : undefined} sub={stats.due_today ? `${stats.due_today} due today` : undefined} />
          <StatCard label="Won" value={stats.won} color="#30d158" sub={stats.win_rate !== null && stats.win_rate !== undefined ? `${stats.win_rate}% win rate` : undefined} />
          {stats.new_from_saved > 0 && <StatCard label="New from saved searches" value={stats.new_from_saved} color="#00d4ff" sub="open Prospect Search" />}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search leads…" style={{ ...inputStyle, flex: "1 1 200px", width: "auto", minWidth: 0 }} />
        <select value={owner} onChange={e => setOwner(e.target.value)} style={{ ...inputStyle, width: "auto" }}>
          <option value="">All owners</option>
          {currentUser && <option value={currentUser}>Mine</option>}
          <option value="__none">No owner</option>
          {(meta?.staff || []).filter(s => s.username !== currentUser).map(s => <option key={s.username} value={s.username}>{s.email}</option>)}
        </select>
        <button className="btn btn-secondary btn-sm" aria-pressed={showClosed} onClick={() => setShowClosed(v => !v)}>Show Won, Lost and Dead</button>
        {tags.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {tags.map(t => {
              const on = tagFilter.includes(t.id);
              return (
                <button key={t.id} className="btn btn-secondary btn-sm" aria-pressed={on} onClick={() => setTagFilter(f => on ? f.filter(x => x !== t.id) : [...f, t.id])} style={on ? { background: t.color, borderColor: t.color, color: "#0b0b0b" } : { color: t.color }}>{t.name}</button>
              );
            })}
            {tagFilter.length > 0 && <button className="btn btn-secondary btn-sm" onClick={() => setTagFilter([])}>Clear tags</button>}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 12 }}><ErrorBox>{error}</ErrorBox></div>

      {loading ? (
        <div style={{ color: "#86868b", textAlign: "center", padding: 60, fontSize: 14 }}>Loading pipeline…</div>
      ) : leads.length === 0 ? (
        <div className="surface-dark" style={{ textAlign: "center", padding: "60px 20px", background: "#1c1c1e", borderRadius: 16, border: "1px solid #2c2c2e" }}>
          <div style={{ color: "#f5f5f7", fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No leads yet</div>
          <div style={{ color: "#86868b", fontSize: 13, marginBottom: 14 }}>Find newly incorporated companies in Prospect Search and add them here.</div>
          <button className="btn btn-primary" onClick={() => navigate("/sales/search")}>Open Prospect Search</button>
        </div>
      ) : (
        <Board
          columns={columns}
          getId={l => l.id}
          renderCard={renderCard}
          onMove={handleMove}
          onOpen={l => setOpenId(l.id)}
          isMobile={isMobile}
          emptyText="No leads"
          selectable={selectMode}
          selectedIds={selected}
          onToggleSelect={toggleSelect}
          onToggleColumn={toggleColumn}
        />
      )}

      {selectMode && (
        <BulkBar
          count={selectedLeads.length}
          deadCount={selectedLeads.filter(l => l.stage === "Dead").length}
          stages={stages}
          staff={meta?.staff || []}
          busy={bulkBusy}
          info={bulkInfo}
          isMobile={isMobile}
          canDelete={can("sales.delete")}
          onTags={() => setShowBulkTags(true)}
          onStage={bulkStage}
          onOwner={owner => bulk({ action: "owner", owner }, owner ? `Owner set to ${owner.split("@")[0]}` : "Owner cleared")}
          onRevive={() => bulk({ action: "revive" }, "Revived")}
          onDelete={bulkDelete}
          onSelectVisible={() => setSelected(new Set(columns.flatMap(c => c.items.map(l => l.id))))}
          onClear={() => setSelected(new Set())}
          onDone={exitSelect}
        />
      )}

      {openId && (
        <LeadDrawer
          key={openId}
          leadId={openId}
          meta={meta}
          isMobile={isMobile}
          onClose={closeDrawer}
          onChanged={onLeadChanged}
          onDeleted={id => { setLeads(prev => prev.filter(l => l.id !== id)); closeDrawer(); load(); }}
          onAskLost={lead => setLostPrompt({ lead, reason: lead.lost_reason || "" })}
          navigate={navigate}
          allTags={tags}
          can={can}
        />
      )}

      {lostPrompt && (
        <Modal title={lostPrompt.bulk ? `Mark ${selectedLeads.length} lead${selectedLeads.length === 1 ? "" : "s"} as Lost` : `Mark ${lostPrompt.lead.company_name} as Lost`} onClose={() => setLostPrompt(null)} footer={<>
          {/* eslint-disable-next-line react/jsx-key */}
          <button className="btn btn-secondary" onClick={() => setLostPrompt(null)}>Cancel</button>
          <button className="btn btn-danger" disabled={!lostPrompt.reason.trim()} onClick={async () => {
            const p = lostPrompt; setLostPrompt(null);
            if (p.bulk) { await bulk({ action: "stage", stage: "Lost", lost_reason: p.reason.trim() }, "Marked as Lost"); return; }
            await moveLead(p.lead, "Lost", { lost_reason: p.reason.trim() });
            if (openId === p.lead.id) { setOpenId(null); setTimeout(() => setOpenId(p.lead.id), 0); }
          }}>Mark as Lost</button>
        </>}>
          <div>
            <label style={labelStyle}>Reason</label>
            <input autoFocus value={lostPrompt.reason} onChange={e => setLostPrompt(p => ({ ...p, reason: e.target.value }))}
              placeholder="e.g. No response, chose another provider, not a fit" style={inputStyle} maxLength={300} />
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {["No response", "Not interested", "Chose another provider", "Not a fit", "Company closed"].map(r => (
              <button className="btn btn-secondary btn-sm" aria-pressed={lostPrompt.reason === r} key={r} onClick={() => setLostPrompt(p => ({ ...p, reason: r }))}>{r}</button>
            ))}
          </div>
        </Modal>
      )}

      {showAdd && <AddLeadModal meta={meta} onClose={() => setShowAdd(false)} onAdded={id => { setShowAdd(false); load(); setOpenId(id); }} />}
      {showSuppression && <SuppressionModal onClose={() => setShowSuppression(false)} />}
      {showBulkTags && (
        <BulkTagsModal
          leads={selectedLeads}
          tags={tags}
          busy={bulkBusy}
          onClose={() => setShowBulkTags(false)}
          onApply={async (add, remove) => {
            const bits = [];
            if (add.length) bits.push(`added ${add.length} tag${add.length === 1 ? "" : "s"}`);
            if (remove.length) bits.push(`removed ${remove.length}`);
            await bulk({ action: "tags", add_tag_ids: add, remove_tag_ids: remove }, "Tags " + bits.join(", "));
            setShowBulkTags(false);
          }}
        />
      )}
      {showRules && <SalesRules tags={tags} meta={meta} onClose={() => { setShowRules(false); load(); }} />}
      {showTags && <TagManager tags={tags} colours={meta?.tag_colours} onClose={() => { setShowTags(false); load(); }} onChanged={setTags} />}
    </div>
  );
}

// ── Bulk bar and bulk tags (v2.6) ─────────────────────────────────────────────

function BulkBar({ count, deadCount, stages, staff, busy, info, isMobile, canDelete, onTags, onStage, onOwner, onRevive, onDelete, onSelectVisible, onClear, onDone }) {
  const none = count === 0 || busy;
  return (
    <div className="surface-dark" style={{
      position: "fixed", left: isMobile ? 8 : "50%", right: isMobile ? 8 : "auto", bottom: 16,
      transform: isMobile ? "none" : "translateX(-50%)", zIndex: 800, maxWidth: isMobile ? "none" : "calc(100vw - 48px)",
      background: "#1c1c1e", border: "1px solid #00d4ff", borderRadius: 12, padding: "10px 12px",
      boxShadow: "0 12px 40px rgba(0,0,0,0.55)", display: "flex", flexDirection: "column", gap: 6,
    }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#f5f5f7", whiteSpace: "nowrap" }}>{count} selected</span>
        <button className="btn btn-secondary btn-sm" disabled={busy} onClick={onSelectVisible}>Select all shown</button>
        {count > 0 && <button className="btn btn-secondary btn-sm" disabled={busy} onClick={onClear}>Clear</button>}
        <span style={{ width: 1, height: 22, background: "#3a3a3c", display: isMobile ? "none" : "inline-block" }} />
        <button className="btn btn-primary btn-sm" disabled={none} onClick={onTags}>Tags</button>
        <select value="" disabled={none} onChange={e => onStage(e.target.value)} style={{ ...inputStyle, width: "auto", padding: "5px 8px", fontSize: 12 }} aria-label="Move to stage">
          <option value="">Move to…</option>
          {stages.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value="__keep" disabled={none} onChange={e => e.target.value !== "__keep" && onOwner(e.target.value)} style={{ ...inputStyle, width: "auto", padding: "5px 8px", fontSize: 12 }} aria-label="Set owner">
          <option value="__keep">Owner…</option>
          <option value="">No owner</option>
          {staff.map(s => <option key={s.username} value={s.username}>{s.email}</option>)}
        </select>
        {deadCount > 0 && <button className="btn btn-secondary btn-sm" disabled={busy} onClick={onRevive}>Revive {deadCount}</button>}
        {canDelete && <button className="btn btn-danger btn-sm" disabled={none} onClick={onDelete}>Delete</button>}
        <button className="btn btn-secondary btn-sm" disabled={busy} onClick={onDone}>Done</button>
        {busy && <span className="spinner" />}
      </div>
      {info && <div style={{ fontSize: 12, color: "#64d2ff" }}>{info}</div>}
    </div>
  );
}

// Add, remove or leave each tag across the selected leads.
function BulkTagsModal({ leads, tags, busy, onClose, onApply }) {
  const [choice, setChoice] = useState({});   // tag id -> "add" | "remove"
  const counts = {};
  leads.forEach(l => (l.tags || []).forEach(t => { counts[t.id] = (counts[t.id] || 0) + 1; }));
  const add = Object.keys(choice).filter(k => choice[k] === "add").map(Number);
  const remove = Object.keys(choice).filter(k => choice[k] === "remove").map(Number);
  const set = (id, v) => setChoice(c => ({ ...c, [id]: c[id] === v ? undefined : v }));
  return (
    <Modal title={`Tags on ${leads.length} lead${leads.length === 1 ? "" : "s"}`} onClose={onClose} maxWidth={520} footer={<>
      {/* eslint-disable-next-line react/jsx-key */}
      <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary" disabled={busy || !(add.length || remove.length)} onClick={() => onApply(add, remove)}>
        {busy ? "Applying…" : `Apply${add.length || remove.length ? ` (${add.length ? `add ${add.length}` : ""}${add.length && remove.length ? ", " : ""}${remove.length ? `remove ${remove.length}` : ""})` : ""}`}
      </button>
    </>}>
      {tags.length === 0 && <div style={{ fontSize: 13, color: "#86868b" }}>No tags yet. Make some in Tags first.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {tags.map(t => {
          const n = counts[t.id] || 0;
          return (
            <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 2px", borderBottom: "1px solid #2c2c2e", flexWrap: "wrap" }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: t.color, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: "#f5f5f7", flex: "1 1 120px", minWidth: 0 }}>{t.name}</span>
              <span style={{ fontSize: 11, color: "#86868b", whiteSpace: "nowrap" }}>on {n} of {leads.length}</span>
              <div style={{ display: "flex", gap: 4 }}>
                <button className="btn btn-secondary btn-sm" aria-pressed={choice[t.id] === "add"} disabled={n === leads.length} onClick={() => set(t.id, "add")}>Add</button>
                <button className="btn btn-secondary btn-sm" aria-pressed={choice[t.id] === "remove"} disabled={n === 0} onClick={() => set(t.id, "remove")}>Remove</button>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: "#86868b" }}>Tags you leave alone stay as they are on each lead. Adding a tag starts its follow-up timer and runs any tag rules.</div>
    </Modal>
  );
}

// ── Lead drawer ───────────────────────────────────────────────────────────────

function LeadDrawer({ leadId, meta, isMobile, onClose, onChanged, onDeleted, onAskLost, navigate, allTags, can }) {
  const [lead, setLead] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({});
  const [guesses, setGuesses] = useState([]);
  const [newContact, setNewContact] = useState(null);
  const [activity, setActivity] = useState({ type: "note", body: "" });
  const [sendTo, setSendTo] = useState(null);       // contact
  const [showConvert, setShowConvert] = useState(false);
  const [assess, setAssess] = useState({ running: false, reports: [], shares: [], loaded: false });
  const [copiedShare, setCopiedShare] = useState("");
  const [editName, setEditName] = useState(null);   // v1.8: string while editing
  const [full, setFull] = useState(null);           // full enrich status
  const allow = (p) => !can || can(p);

  const apply = useCallback((l) => {
    setLead(l);
    setForm({
      owner: l.owner || "", value: l.value || "", next_action: l.next_action || "",
      next_action_date: l.next_action_date || "", domain: l.domain || "", notes: l.notes || "",
      company_number: l.company_number || "",
    });
    onChanged(l);
  }, [onChanged]);

  useEffect(() => {
    api(`/api/sales/leads/${leadId}`).then(apply).catch(e => setError(e.message));
  }, [leadId, apply]);

  async function run(label, fn) {
    setBusy(label); setError(""); setInfo("");
    try { return await fn(); }
    catch (e) { setError(e.message); return null; }
    finally { setBusy(""); }
  }

  async function save(fields) {
    const l = await run("save", () => api(`/api/sales/leads/${leadId}`, { method: "PUT", body: fields }));
    if (l) apply(l);
  }

  async function theyReplied(note = "") {
    const l = await run("reply", () => api(`/api/sales/leads/${leadId}/response`, { method: "POST", body: { note } }));
    if (l) { apply(l); setInfo(l.stage === "Dead" ? "Reply logged." : "Reply logged. No-response timers on this lead stop here."); }
    return l;
  }

  async function revive() {
    const l = await run("revive", () => api(`/api/sales/leads/${leadId}/revive`, { method: "POST", body: {} }));
    if (l) { apply(l); setInfo(`Revived to ${l.stage}.`); }
  }

  async function setTags(ids) {
    const l = await run("tags", () => api(`/api/sales/leads/${leadId}/tags`, { method: "PUT", body: { tag_ids: ids } }));
    if (l) apply(l);
  }

  async function fullEnrich() {
    const d = await run("full", () => api(`/api/sales/leads/${leadId}/enrich-full`, { method: "POST" }));
    if (d) { setFull(d); if (!d.running) api(`/api/sales/leads/${leadId}`).then(apply).catch(() => {}); }
  }

  useEffect(() => {
    api(`/api/sales/leads/${leadId}/enrich-full`).then(d => { if (d && (d.running || d.finished)) setFull(d); }).catch(() => {});
  }, [leadId]);

  useEffect(() => {
    if (!full || !full.running) return;
    const id = setInterval(async () => {
      try {
        const d = await api(`/api/sales/leads/${leadId}/enrich-full`);
        setFull(d);
        if (!d.running) { clearInterval(id); api(`/api/sales/leads/${leadId}`).then(apply).catch(() => {}); }
      } catch { clearInterval(id); }
    }, 5000);
    return () => clearInterval(id);
  }, [full, leadId, apply]);

  function saveIfChanged(field) {
    if (!lead) return;
    const before = field === "value" ? Number(lead.value || 0) : (lead[field] || "");
    const after = field === "value" ? Number(form.value || 0) : (form[field] || "");
    if (before !== after) save({ [field]: form[field] });
  }

  async function enrich() {
    const l = await run("enrich", () => api(`/api/sales/leads/${leadId}/enrich`, { method: "POST" }));
    if (l) {
      apply(l);
      if ((l.guesses || []).length) setGuesses(l.guesses);
      if ((l.enrich_notes || []).length) setInfo(l.enrich_notes.join(". "));
    }
  }

  async function discover() {
    const l = await run("discover", () => api(`/api/sales/leads/${leadId}/discover-emails`, { method: "POST" }));
    if (l) {
      apply(l); setGuesses(l.guesses || []);
      if (l.hunter_error) setInfo(`Hunter: ${l.hunter_error}`);
      else setInfo(`Hunter found ${l.hunter_added} new address${l.hunter_added === 1 ? "" : "es"}.`);
    }
  }

  async function addContact(c) {
    const l = await run("contact", () => api(`/api/sales/leads/${leadId}/contacts`, { method: "POST", body: c }));
    if (l) { apply(l); setNewContact(null); setGuesses(g => g.filter(x => x.email !== c.email)); }
  }

  async function editContact(id, body, method = "PUT") {
    const l = await run("contact", () => api(`/api/sales/contacts/${id}`, { method, body: method === "PUT" ? body : undefined }));
    if (l) apply(l);
  }

  async function addActivity() {
    if (!activity.body.trim()) return;
    if (activity.type === "reply") {
      const r = await theyReplied(activity.body.trim());
      if (r) setActivity({ type: "note", body: "" });
      return;
    }
    const l = await run("activity", () => api(`/api/sales/leads/${leadId}/activities`, { method: "POST", body: activity }));
    if (l) { apply(l); setActivity({ type: activity.type, body: "" }); }
  }

  async function sendEmail(contact, confirmRepeat = false) {
    setBusy("send"); setError("");
    try {
      const l = await api(`/api/sales/leads/${leadId}/send-email`, { method: "POST", body: { contact_id: contact.id, confirm_repeat: confirmRepeat } });
      apply(l); setSendTo(null); setInfo(`Email sent to ${contact.email}.`);
    } catch (e) {
      if (e.data && e.data.needs_confirm) {
        if (window.confirm(`${e.message}. Send again anyway?`)) { setBusy(""); return sendEmail(contact, true); }
        setSendTo(null);
      } else setError(e.message);
    } finally { setBusy(""); }
  }

  async function del() {
    if (!window.confirm(`Delete ${lead.company_name} and all its contacts and activity? This cannot be undone.`)) return;
    const ok = await run("delete", () => api(`/api/sales/leads/${leadId}`, { method: "DELETE" }));
    if (ok) onDeleted(leadId);
  }

  const loadAssess = useCallback(async () => {
    try {
      const d = await api(`/api/sales/leads/${leadId}/assessment-reports`);
      setAssess({ running: d.running, reports: d.reports || [], slug: d.slug || "", shares: d.shares || [], loaded: true });
      return d;
    } catch { setAssess(a => ({ ...a, loaded: true })); return null; }
  }, [leadId]);

  useEffect(() => { loadAssess(); }, [loadAssess]);

  // While an assessment is running, poll for completion and the report.
  useEffect(() => {
    if (!assess.running) return;
    const id = setInterval(async () => {
      const d = await loadAssess();
      if (d && !d.running) clearInterval(id);
    }, 15000);
    return () => clearInterval(id);
  }, [assess.running, loadAssess]);

  async function runAssess() {
    if (!lead.domain) { setError("Set or discover a domain before running an assessment"); return; }
    setBusy("assess"); setError(""); setInfo("");
    try {
      await api(`/api/sales/leads/${leadId}/assess`, { method: "POST", body: {} });
      setInfo("Assessment started. It runs in the background; the report appears here when it finishes.");
      setAssess(a => ({ ...a, running: true }));
      api(`/api/sales/leads/${leadId}`).then(apply).catch(() => {});
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }

  async function shareReport() {
    setBusy("share"); setError(""); setInfo("");
    try {
      const d = await api(`/api/sales/leads/${leadId}/share-report`, { method: "POST", body: {} });
      await loadAssess();
      if (d.url && navigator.clipboard) {
        navigator.clipboard.writeText(d.url).then(() => { setCopiedShare(d.token); setTimeout(() => setCopiedShare(""), 2500); }).catch(() => {});
      }
      setInfo("Share link ready and copied. It is a public link — anyone with it can view the report.");
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }

  const copyShare = (url, token) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(url).then(() => { setCopiedShare(token); setTimeout(() => setCopiedShare(""), 2500); }).catch(() => {});
  };

  const staff = meta?.staff || [];
  const stages = meta?.stages || [];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 900, display: "flex", alignItems: "stretch" }}>
      {!isMobile && <div style={{ flex: 1, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(2px)" }} onClick={onClose} />}
      <div style={{ width: isMobile ? "100vw" : "min(640px, 95vw)", background: "#111", borderLeft: "1px solid #2c2c2e", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: isMobile ? "14px 14px" : "18px 22px", borderBottom: "1px solid #2c2c2e", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            {lead ? (
              <>
                {/* eslint-disable-next-line react/jsx-key */}
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
                  <Badge label={lead.stage} color={STAGE_META[lead.stage]?.color} bg={STAGE_META[lead.stage]?.bg} />
                  {lead.score_band && <ScoreBadge score={lead.score} band={lead.score_band} size="lg" />}
                  {lead.company_number && <span style={{ fontFamily: "'SF Mono',monospace", fontSize: 11, color: "#00d4ff" }}>{lead.company_number}</span>}
                  {lead.converted_slug && <Badge label="Client" color="#30d158" bg="rgba(48,209,88,0.12)" />}
                </div>
                {editName === null ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#f5f5f7", lineHeight: 1.3, overflowWrap: "anywhere" }}>{lead.company_name}</h2>
                    <button className="btn btn-quiet btn-lg btn-icon" style={{ flexShrink: 0 }} title="Edit name" onClick={() => setEditName(lead.company_name)}><i className="ti ti-pencil" /></button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input autoFocus value={editName} maxLength={200} onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && editName.trim()) { save({ company_name: editName.trim() }); setEditName(null); } if (e.key === "Escape") setEditName(null); }}
                      style={{ ...inputStyle, fontSize: 15, fontWeight: 700, flex: 1, minWidth: 0 }} />
                    <button className="btn btn-secondary btn-sm" disabled={!editName.trim()} onClick={() => { save({ company_name: editName.trim() }); setEditName(null); }}>Save</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditName(null)}>Cancel</button>
                  </div>
                )}
                <PostureChips posture={lead.email_posture} mxProvider={lead.mx_provider} />
                {lead.engagement && (lead.engagement.sent > 0) && (
                  <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 11, color: "#86868b" }}>
                    <span><i className="ti ti-send" style={{ marginRight: 4 }} />{lead.engagement.sent} sent</span>
                    <span style={{ color: lead.engagement.opens ? "#30d158" : "#86868b" }}>
                      <i className="ti ti-mail-opened" style={{ marginRight: 4 }} />{lead.engagement.opens} opened
                    </span>
                    <span style={{ color: lead.engagement.clicks ? "#0a84ff" : "#86868b" }}>
                      <i className="ti ti-click" style={{ marginRight: 4 }} />{lead.engagement.clicks} clicked
                    </span>
                  </div>
                )}
                <div style={{ fontSize: 11, color: "#86868b", marginTop: 6 }}>Updated {fmtUpdated(lead.updated_at)}</div>
              </>
            ) : <span className="spinner" />}
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            {lead && allow("sales.delete") && <button className="btn btn-quiet btn-lg btn-icon" style={{ flexShrink: 0 }} onClick={del} title="Delete lead"><i className="ti ti-trash" /></button>}
            <button className="btn btn-quiet btn-icon" style={{ flexShrink: 0 }} onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        {lead && (
          <div className="thin-scroll" style={{ flex: 1, overflow: "auto", padding: isMobile ? 14 : "18px 22px", display: "flex", flexDirection: "column", gap: 18 }}>
            {(error || info) && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <ErrorBox>{error}</ErrorBox>
                {info && <div style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.25)", borderRadius: 8, padding: "9px 12px", color: "#64d2ff", fontSize: 13 }}>{info}</div>}
              </div>
            )}

            {lead.stage === "Dead" && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "rgba(99,99,102,0.16)", border: "1px solid #3a3a3c", borderRadius: 8, padding: "9px 12px" }}>
                <span style={{ fontSize: 13, color: "#d1d1d6", flex: "1 1 200px" }}>
                  Dead{lead.dead_at ? ` since ${fmtUpdated(lead.dead_at)}` : ""}{lead.dead_from_stage ? `. Went quiet in ${lead.dead_from_stage}.` : "."}
                </span>
                {allow("sales.edit") && <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={revive}>
                  {busy === "revive" ? "Reviving…" : `Revive to ${["New", "Researching", "Contacted", "Engaged", "Proposal"].includes(lead.dead_from_stage) ? lead.dead_from_stage : "Contacted"}`}
                </button>}
              </div>
            )}

            {/* Quick actions */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {allow("sales.edit") && <button className="btn btn-secondary" disabled={!!busy} onClick={() => theyReplied()}
                title="Log that the prospect replied. Counts as a response for pipeline rules">{busy === "reply" ? "Logging…" : "They replied"}</button>}
              {allow("sales.enrich") && <button className="btn btn-secondary" disabled={!!busy} onClick={enrich}>{busy === "enrich" ? "Enriching…" : lead.enriched_at ? "Enrich again" : "Enrich"}</button>}
              {allow("sales.enrich") && <button className="btn btn-secondary" disabled={!!busy || !lead.domain} onClick={fullEnrich}
                title={!lead.domain ? "Find the website first" : "Breach exposure, subdomains and the site's internet-facing services"}>
                {busy === "full" || (full && full.running) ? "Deep enrich running…" : "Full enrich"}
              </button>}
              {allow("sales.enrich") && <button className="btn btn-secondary" disabled={!!busy || !lead.domain || !meta?.hunter_configured} onClick={discover}
                title={!meta?.hunter_configured ? "HUNTER_KEY is not set" : !lead.domain ? "Set or discover a domain first" : "Search Hunter for addresses at this domain"}>
                {busy === "discover" ? "Searching…" : "Find emails"}
              </button>}
              {!lead.converted_slug ? (
                allow("sales.convert") && <button className="btn btn-secondary" onClick={() => setShowConvert(true)}>Add to Client Management</button>
              ) : (
                <button className="btn btn-secondary" onClick={() => navigate(`/clients/${lead.converted_slug}`)}>Open client</button>
              )}
              {allow("sales.assess") && <button className="btn btn-secondary" disabled={!!busy || !lead.domain} onClick={runAssess}
                title={!lead.domain ? "Set or discover a domain first" : "Run an External Cyber Security Assessment on this company"}>
                {busy === "assess" ? "Starting…" : assess.running ? "Assessment running…" : (assess.reports.length ? "Re-run assessment" : "Run assessment")}
              </button>}
              <a className="btn btn-secondary" href={lead.ch_url} target="_blank" rel="noopener noreferrer">Companies House</a>
            </div>

            <TagEditor lead={lead} allTags={allTags} canEdit={allow("sales.edit")} onChange={setTags} />

            {/* Deal fields */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Stage</label>
                <select value={lead.stage} disabled={!!busy} onChange={e => e.target.value === "Lost" ? onAskLost(lead) : save({ stage: e.target.value })} style={{ ...inputStyle, color: STAGE_META[lead.stage]?.color }}>
                  {stages.map(s => <option key={s}>{s}</option>)}
                </select>
                {lead.stage === "Lost" && lead.lost_reason && <div style={{ fontSize: 11, color: "#ff453a", marginTop: 4 }}>Reason: {lead.lost_reason}</div>}
                <div style={{ fontSize: 11, color: "#86868b", marginTop: 4 }}>
                  {lead.last_response_at ? `Last response ${fmtUpdated(lead.last_response_at)}` : "No response recorded yet"}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Owner</label>
                <select value={form.owner} onChange={e => { setForm(f => ({ ...f, owner: e.target.value })); save({ owner: e.target.value }); }} style={inputStyle}>
                  <option value="">No owner</option>
                  {staff.map(s => <option key={s.username} value={s.username}>{s.email}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Estimated value (£ per year)</label>
                <input type="number" min="0" step="50" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} onBlur={() => saveIfChanged("value")} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Next action date</label>
                <input type="date" value={form.next_action_date} onChange={e => { setForm(f => ({ ...f, next_action_date: e.target.value })); save({ next_action_date: e.target.value }); }} style={inputStyle} />
              </div>
              <div style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
                <label style={labelStyle}>Next action</label>
                <input value={form.next_action} onChange={e => setForm(f => ({ ...f, next_action: e.target.value }))} onBlur={() => saveIfChanged("next_action")} onKeyDown={e => e.key === "Enter" && e.target.blur()} placeholder="e.g. Call the director, send proposal" style={inputStyle} maxLength={300} />
              </div>
            </div>

            {/* Company */}
            <div>
              <div style={sectionTitle}>Company</div>
              <div className="surface-dark" style={{ background: "#1c1c1e", border: "1px solid #2c2c2e", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 6, fontSize: 13, color: "#d1d1d6" }}>
                <div>
                  <label style={labelStyle}>
                    Companies House number {!lead.company_number && <span style={{ color: "#ff9f0a", textTransform: "none", letterSpacing: 0 }}>· not set, Enrich will try to find it</span>}
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={form.company_number || ""} onChange={e => setForm(f => ({ ...f, company_number: e.target.value.toUpperCase() }))}
                      onKeyDown={e => e.key === "Enter" && form.company_number !== (lead.company_number || "") && save({ company_number: form.company_number })}
                      placeholder="e.g. SC123456" maxLength={10} style={{ ...inputStyle, flex: 1, minWidth: 0, fontFamily: "'SF Mono',monospace" }} />
                    <button className="btn btn-secondary btn-sm" disabled={!!busy || (form.company_number || "") === (lead.company_number || "")} onClick={() => save({ company_number: form.company_number })}>
                      {busy === "save" ? "Looking up…" : "Look up"}
                    </button>
                  </div>
                </div>
                {!lead.company_number && (lead.enrich_data?.ch_candidates || []).length > 0 && (
                  <div className="surface-dark" style={{ background: "#161616", border: "1px dashed #3a3a3c", borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: 11, color: "#ff9f0a", marginBottom: 6 }}>Which company is it? These match the name on Companies House.</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {lead.enrich_data.ch_candidates.map(c => (
                        <div key={c.company_number} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, color: "#f5f5f7" }}>{c.name} <span style={{ color: "#86868b", fontFamily: "'SF Mono',monospace" }}>{c.company_number}</span>
                              {c.status && c.status !== "active" && <span style={{ color: "#ff453a" }}> · {c.status}</span>}</div>
                            <div style={{ fontSize: 11, color: "#86868b", overflowWrap: "anywhere" }}>{c.address}{c.incorporated ? ` · since ${fmtDate(c.incorporated)}` : ""}</div>
                          </div>
                          <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => save({ company_number: c.company_number })}>Use</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {lead.address && <div style={{ overflowWrap: "anywhere" }}>{lead.address}</div>}
                <div style={{ color: "#86868b", fontSize: 12 }}>
                  {lead.incorporated ? `Incorporated ${fmtDate(lead.incorporated)}` : ""}{(lead.sectors || []).length ? ` · ${lead.sectors.join(", ")}` : ""}
                  {(lead.sic_codes || []).length ? ` · SIC ${lead.sic_codes.join(", ")}` : ""}
                </div>
                <div style={{ marginTop: 6 }}>
                  <label style={labelStyle}>
                    Domain {lead.domain_status === "possible" && <span style={{ color: "#ff9f0a", textTransform: "none", letterSpacing: 0 }}>· possible match, check before using</span>}
                    {lead.domain_status === "confirmed" && <span style={{ color: "#30d158", textTransform: "none", letterSpacing: 0 }}>· confirmed</span>}
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={form.domain} onChange={e => setForm(f => ({ ...f, domain: e.target.value }))} placeholder="example.co.uk" style={{ ...inputStyle, flex: 1, minWidth: 0 }} />
                    <button className="btn btn-secondary btn-sm" disabled={!!busy || (form.domain === lead.domain && lead.domain_status === "confirmed")} onClick={() => save({ domain: form.domain })}>
                      {form.domain && form.domain === lead.domain && lead.domain_status === "possible" ? "Confirm" : "Save"}
                    </button>
                    {lead.domain && <a className="btn btn-secondary btn-sm" href={`https://${lead.domain}`} target="_blank" rel="noopener noreferrer" title="Open website"><i className="ti ti-external-link" /></a>}
                  </div>
                  {(lead.domain_candidates || []).length > 1 && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                      {lead.domain_candidates.map(c => (
                        <span key={c.domain} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                          <button className="btn btn-secondary btn-sm" aria-pressed={c.domain === lead.domain} onClick={() => setForm(f => ({ ...f, domain: c.domain }))} title={`${c.reason ? c.reason + ". " : ""}${c.mx ? "Has mail records" : "No mail records"}, ${c.web ? "has a website address" : "no website address"}`}>
                            {c.domain}{c.mx ? " · mail" : ""}{c.from_search ? " · search" : ""}
                          </button>
                          <button className="btn btn-quiet btn-sm" aria-label={`Dismiss ${c.domain}`} title="Not this company. Remove and don't suggest again" disabled={!!busy}
                            onClick={() => {
                              if (c.domain === lead.domain && !window.confirm(`${c.domain} is this lead's current website. Remove it?`)) return;
                              save({ dismiss_domain: c.domain });
                            }}
                           >
                            <i className="ti ti-x" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Enrichment insights (v1.6) */}
            <EnrichInsights lead={lead} />

            {full && full.error && <ErrorBox>Full enrich: {full.error}</ErrorBox>}
            <FullEnrichInsights lead={lead} running={full && full.running} />

            {/* Assessment */}
            {(assess.running || assess.reports.length > 0) && (
              <div>
                <div style={sectionTitle}>External Cyber Security Assessment</div>
                <div className="surface-dark" style={{ background: "#1c1c1e", border: "1px solid #2c2c2e", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  {assess.running && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#64d2ff" }}>
                      <span className="spinner" /> Scanning in the background. This can take 15–30 minutes.
                    </div>
                  )}
                  {assess.reports.map(r => (
                    <div key={r.filename} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minWidth: 0 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: "#f5f5f7" }}>
                          {r.grade ? `Grade ${r.grade}` : "Report"}{r.is_internal ? " · internal" : ""}
                        </div>
                        <div style={{ fontSize: 11, color: "#86868b" }}>{fmtUpdated(r.date)}</div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        {assess.slug && (
                          <a className="btn btn-secondary btn-sm" href={`/api/clients/${assess.slug}/assessment-reports/${encodeURIComponent(r.filename)}?view=1`}
                            target="_blank" rel="noopener noreferrer">View</a>
                        )}
                        {!r.is_internal && (
                          <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={shareReport}>
                            {busy === "share" ? "…" : "Share"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(assess.shares || []).length > 0 && (
                    <div style={{ borderTop: "1px solid #2c2c2e", paddingTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ fontSize: 10, color: "#86868b", textTransform: "uppercase", letterSpacing: "0.07em" }}>Shared links</div>
                      {assess.shares.map(sh => (
                        <div key={sh.token} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, color: "#64d2ff", fontFamily: "'SF Mono',monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={sh.url}>{sh.url}</div>
                            <div style={{ fontSize: 10, color: sh.views > 0 ? "#30d158" : "#86868b", marginTop: 2 }}>
                              {sh.views > 0 ? `Opened ${sh.views}× · last ${fmtUpdated(sh.last_view)}` : "Not opened yet"}
                            </div>
                          </div>
                          <button className="btn btn-secondary btn-sm" onClick={() => copyShare(sh.url, sh.token)}>{copiedShare === sh.token ? "Copied" : "Copy"}</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Contacts */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ ...sectionTitle, margin: 0 }}>Contacts ({lead.contacts.length})</div>
                <button className="btn btn-secondary btn-sm" onClick={() => setNewContact({ name: "", role: "", email: "", phone: "" })}>+ Add</button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {lead.contacts.length === 0 && !newContact && <div style={{ fontSize: 12, color: "#86868b" }}>No contacts yet. Enrich pulls directors from Companies House; Find emails searches Hunter.</div>}
                {lead.contacts.map(c => (
                  <ContactRow key={c.id} c={c} busy={busy}
                    onSend={() => setSendTo(c)}
                    onToggleDnc={() => editContact(c.id, { do_not_contact: !c.do_not_contact })}
                    onSetEmail={email => editContact(c.id, { email })}
                    onDelete={() => window.confirm(`Remove ${c.name || c.email}?`) && editContact(c.id, null, "DELETE")}
                  />
                ))}
                {newContact && (
                  <div className="surface-dark" style={{ background: "#1c1c1e", border: "1px solid #00d4ff55", borderRadius: 10, padding: 12, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 8 }}>
                    <input placeholder="Name" value={newContact.name} onChange={e => setNewContact(n => ({ ...n, name: e.target.value }))} style={inputStyle} />
                    <input placeholder="Role" value={newContact.role} onChange={e => setNewContact(n => ({ ...n, role: e.target.value }))} style={inputStyle} />
                    <input placeholder="Email" type="email" value={newContact.email} onChange={e => setNewContact(n => ({ ...n, email: e.target.value }))} style={inputStyle} />
                    <input placeholder="Phone" value={newContact.phone} onChange={e => setNewContact(n => ({ ...n, phone: e.target.value }))} style={inputStyle} />
                    <div style={{ gridColumn: isMobile ? "auto" : "1 / -1", display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setNewContact(null)}>Cancel</button>
                      <button className="btn btn-primary" disabled={!!busy} onClick={() => addContact({ ...newContact, source: "manual" })}>Add contact</button>
                    </div>
                  </div>
                )}
                {guesses.length > 0 && (
                  <div className="surface-dark" style={{ background: "#161616", border: "1px dashed #3a3a3c", borderRadius: 10, padding: 10 }}>
                    <div style={{ fontSize: 11, color: "#ff9f0a", marginBottom: 6 }}>Guessed addresses. These are not verified; add one only if you are happy to try it.</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {guesses.map(g => (
                        <div key={g.email} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, minWidth: 0 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12, color: "#d1d1d6", overflowWrap: "anywhere" }}>{g.email}</div>
                            <div style={{ fontSize: 11, color: "#86868b" }}>{g.name ? `${g.name} · ` : ""}{g.basis}</div>
                          </div>
                          <button className="btn btn-secondary btn-sm" onClick={() => addContact({ email: g.email, name: g.name, source: "guess" })}>Add</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <div style={sectionTitle}>Notes</div>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} onBlur={() => saveIfChanged("notes")} rows={3}
                placeholder="Background, what they need, who referred them…" style={{ ...inputStyle, resize: "vertical", minHeight: 70 }} />
            </div>

            {/* Activity */}
            <div>
              <div style={sectionTitle}>Activity</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                <select value={activity.type} onChange={e => setActivity(a => ({ ...a, type: e.target.value }))} style={{ ...inputStyle, width: isMobile ? "100%" : 110, flexShrink: 0 }}>
                  <option value="note">Note</option><option value="call">Call</option><option value="meeting">Meeting</option><option value="email">Email</option><option value="reply">Reply</option>
                </select>
                <input value={activity.body} onChange={e => setActivity(a => ({ ...a, body: e.target.value }))} onKeyDown={e => e.key === "Enter" && addActivity()}
                  placeholder="What happened?" style={{ ...inputStyle, flex: 1, minWidth: 0 }} />
                <button className="btn btn-primary" disabled={!!busy || !activity.body.trim()} onClick={addActivity}>Log</button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {lead.activities.map(a => (
                  <div key={a.id} style={{ display: "flex", gap: 10, padding: "8px 2px", borderBottom: "1px solid #1c1c1e", minWidth: 0 }}>
                    <i className={`ti ${ACTIVITY_META[a.type]?.icon || "ti-point"}`} style={{ color: a.type === "stage_change" ? "#00d4ff" : a.type === "rule" ? "#ffd60a" : a.type === "reply" ? "#30d158" : a.type === "system" ? "#636366" : "#d1d1d6", fontSize: 15, marginTop: 1, flexShrink: 0 }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, color: a.type === "system" ? "#86868b" : "#f5f5f7", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{a.body}</div>
                      <div style={{ fontSize: 11, color: "#636366", marginTop: 2 }} title={fmtUpdated(a.created_at)}>
                        {ACTIVITY_META[a.type]?.label || a.type} · {a.created_by ? `${a.created_by.split("@")[0]} · ` : ""}{ago(a.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {sendTo && lead && (
        <Modal title="Send outreach email" onClose={() => setSendTo(null)} footer={<>
          {/* eslint-disable-next-line react/jsx-key */}
          <button className="btn btn-secondary" onClick={() => setSendTo(null)}>Cancel</button>
          <button className="btn btn-primary" disabled={busy === "send" || !meta?.email_configured} onClick={() => sendEmail(sendTo)}>{busy === "send" ? "Sending…" : "Send"}</button>
        </>}>
          <div style={{ fontSize: 13, color: "#d1d1d6", lineHeight: 1.6 }}>
            Send the <strong>Prospect Outreach (Sales)</strong> template to <strong style={{ overflowWrap: "anywhere" }}>{sendTo.email}</strong>
            {sendTo.name ? ` (${sendTo.name})` : ""} for {lead.company_name}.
          </div>
          {sendTo.source === "guess" && <div style={{ fontSize: 12, color: "#ff9f0a" }}>This address is a guess and may bounce.</div>}
          <div style={{ fontSize: 12, color: "#86868b", lineHeight: 1.6 }}>
            The wording is edited in System Admin under Email Templates. An unsubscribe link is added automatically, and the lead moves to Contacted if it is at New or Researching.
            Sole traders and partnerships count as individuals under PECR, so only email them if they have agreed to it.
          </div>
          {!meta?.email_configured && <ErrorBox>SMTP2GO_API_KEY is not set, so email cannot be sent.</ErrorBox>}
        </Modal>
      )}

      {showConvert && lead && (
        <ConvertModal lead={lead} onClose={() => setShowConvert(false)} onDone={l => { apply(l); setShowConvert(false); setInfo(`Client ${l.company_name || (l.converted_slug || '').replace(/_/g, ' ')} created.`); }} />
      )}
    </div>
  );
}

function ContactRow({ c, busy, onSend, onToggleDnc, onSetEmail, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(c.email || "");
  const src = SOURCE_META[c.source] || SOURCE_META.manual;
  const blocked = c.do_not_contact || c.suppressed;
  return (
    <div className="surface-dark" style={{ background: "#1c1c1e", border: "1px solid #2c2c2e", borderRadius: 10, padding: "10px 12px", minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#f5f5f7", overflowWrap: "anywhere" }}>
            {c.name || c.email || "Unnamed"}{c.role && <span style={{ fontWeight: 400, color: "#86868b" }}> · {c.role}</span>}
          </div>
          {c.email && !editing && <div style={{ fontSize: 12, color: blocked ? "#636366" : "#64d2ff", overflowWrap: "anywhere", textDecoration: blocked ? "line-through" : "none" }}>{c.email}</div>}
          {c.phone && <div style={{ fontSize: 12, color: "#86868b" }}>{c.phone}</div>}
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 5 }}>
            <Badge label={src.label} color={src.color} bg={`${src.color}1f`} />
            {c.source === "hunter" && c.confidence > 0 && <Badge label={`${c.confidence}% confidence`} />}
            {c.do_not_contact ? <Badge label="Do not contact" color="#ff453a" bg="rgba(255,69,58,0.12)" /> : null}
            {c.suppressed && !c.do_not_contact ? <Badge label="Suppressed" color="#ff453a" bg="rgba(255,69,58,0.12)" /> : null}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {c.email && !blocked && <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={onSend}>Email</button>}
          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(e => !e)} title="Edit email"><i className="ti ti-pencil" /></button>
          <button className="btn btn-secondary btn-sm" onClick={onToggleDnc} title={c.do_not_contact ? "Allow contact" : "Mark do not contact"}><i className={`ti ${c.do_not_contact ? "ti-bell" : "ti-bell-off"}`} /></button>
          <button className="btn btn-secondary btn-sm" onClick={onDelete} title="Remove"><i className="ti ti-x" /></button>
        </div>
      </div>
      {editing && (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="name@company.co.uk" style={{ ...inputStyle, flex: 1, minWidth: 0 }} />
          <button className="btn btn-secondary btn-sm" onClick={() => { onSetEmail(email.trim()); setEditing(false); }}>Save</button>
        </div>
      )}
    </div>
  );
}

// ── Enrichment insights (v1.6) ────────────────────────────────────────────────

function fmtShortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function InsightRow({ label, children, tone }) {
  return (
    <div style={{ display: "flex", gap: 10, fontSize: 12, lineHeight: 1.5 }}>
      <div style={{ width: 120, flexShrink: 0, color: "#86868b" }}>{label}</div>
      <div style={{ flex: 1, minWidth: 0, color: tone || "#d1d1d6", overflowWrap: "anywhere" }}>{children}</div>
    </div>
  );
}

function EnrichInsights({ lead }) {
  const ed = lead.enrich_data || {};
  const co = ed.company || {};
  const web = ed.web || {};
  const dc = ed.domain_check || {};
  const posture = lead.email_posture || {};
  const psc = (ed.psc || []).filter(p => p.name);
  const reasons = ed.score_reasons || [];
  if (!lead.enriched_at && !Object.keys(ed).length) return null;
  const warn = "#ff9f0a", bad = "#ff453a", good = "#30d158";
  const socials = Object.entries(web.socials || {});
  const cert = web.cert_days;
  return (
    <div>
      <div style={sectionTitle}>What Enrich found{lead.enriched_at ? <span style={{ textTransform: "none", letterSpacing: 0, color: "#86868b", fontWeight: 400 }}> · {fmtUpdated(lead.enriched_at)}</span> : null}</div>
      <div className="surface-dark" style={{ background: "#1c1c1e", border: "1px solid #2c2c2e", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 6 }}>
        {dc.domain && (
          <InsightRow label="Website" tone={dc.score >= 85 ? good : dc.score >= 45 ? undefined : warn}>
            {dc.domain} — {dc.reason}{dc.via ? ` (via ${dc.via})` : ""}
          </InsightRow>
        )}
        {ed.search && (
          <InsightRow label="Web search" tone={ed.search.used ? undefined : ed.search.reason && ed.search.reason.startsWith("Not needed") ? "#86868b" : warn}>
            {ed.search.used
              ? `${ed.search.results} result${ed.search.results === 1 ? "" : "s"} used${ed.search.cached ? " (saved from an earlier search, no credit used)" : ""}`
              : `Not used: ${ed.search.reason}`}
          </InsightRow>
        )}
        {co.size && <InsightRow label="Size">{co.size}{co.last_accounts ? ` · accounts to ${fmtShortDate(co.last_accounts)}` : ""}</InsightRow>}
        {co.status && co.status !== "active" && <InsightRow label="Status" tone={bad}>{co.status}</InsightRow>}
        {(co.insolvency || co.liquidated) && <InsightRow label="Warning" tone={bad}>Insolvency or liquidation history at Companies House</InsightRow>}
        {(co.accounts_overdue || co.cs_overdue) && (
          <InsightRow label="Filings" tone={warn}>
            {[co.accounts_overdue && "Accounts overdue", co.cs_overdue && "Confirmation statement overdue"].filter(Boolean).join(" · ")}
          </InsightRow>
        )}
        {!co.accounts_overdue && co.accounts_next_due && <InsightRow label="Next accounts">Due {fmtShortDate(co.accounts_next_due)}</InsightRow>}
        {co.charges && <InsightRow label="Charges">Has registered charges (secured lending)</InsightRow>}
        {psc.length > 0 && (
          <InsightRow label="Owners">
            {psc.map(p => `${p.name}${(p.control || []).length ? ` (${p.control[0]})` : ""}`).join(" · ")}
          </InsightRow>
        )}
        {(web.phones || []).length > 0 && <InsightRow label="Phone">{web.phones.join(" · ")}</InsightRow>}
        {(web.emails || []).length > 0 && <InsightRow label="Published email">{web.emails.join(" · ")}</InsightRow>}
        {socials.length > 0 && (
          <InsightRow label="Social">
            {socials.map(([k, u], i) => (
              <span key={k}>{i ? " · " : ""}<a href={u} target="_blank" rel="noopener noreferrer" style={{ color: "#64d2ff", textDecoration: "none" }}>{({ linkedin: "LinkedIn", facebook: "Facebook", instagram: "Instagram", x: "X" })[k] || k}</a></span>
            ))}
          </InsightRow>
        )}
        {ed.platform && <InsightRow label="Website platform">{ed.platform}</InsightRow>}
        {!ed.platform && (web.wordpress || web.generator) && <InsightRow label="Website platform">{web.wordpress ? "WordPress" : web.generator}</InsightRow>}
        {ed.http && ed.http.https_redirect === false && <InsightRow label="HTTPS" tone="#ff9f0a">Does not redirect visitors to HTTPS</InsightRow>}
        {ed.http && ed.http.headers && (
          <InsightRow label="Security headers">
            {["strict-transport-security","content-security-policy","x-frame-options","x-content-type-options"].filter(h => ed.http.headers[h]).length} of 4 set
          </InsightRow>
        )}
        {ed.domain_reg && (ed.domain_reg.registrar || ed.domain_reg.expires) && (
          <InsightRow label="Domain" tone={typeof ed.domain_reg.expires_days === "number" && ed.domain_reg.expires_days < 60 ? "#ff9f0a" : undefined}>
            {[ed.domain_reg.registrar, ed.domain_reg.expires ? `expires ${fmtShortDate(ed.domain_reg.expires)}` : ""].filter(Boolean).join(" · ")}
          </InsightRow>
        )}
        {cert !== undefined && cert !== null && (
          <InsightRow label="Certificate" tone={cert === "invalid" || (typeof cert === "number" && cert < 14) ? bad : typeof cert === "number" && cert < 30 ? warn : undefined}>
            {cert === "invalid" ? "Does not verify" : cert < 0 ? `Expired ${-cert} days ago` : `Expires in ${cert} days`}
          </InsightRow>
        )}
        {posture.mx_provider && posture.mx_provider !== "unknown" && (
          <InsightRow label="Email">
            {posture.mx_provider} · DMARC {posture.dmarc} · SPF {posture.spf}
            {posture.dkim ? ` · DKIM ${posture.dkim === "present" ? `present (${posture.dkim_selector})` : posture.dkim}` : ""}
            {posture.mta_sts !== undefined ? ` · MTA-STS ${posture.mta_sts ? "yes" : "no"}` : ""}
          </InsightRow>
        )}
        {reasons.length > 0 && <InsightRow label={`Score ${lead.score || 0}`}>{reasons.join(" · ")}</InsightRow>}
      </div>
      {(ed.hooks || []).length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={sectionTitle}>Talking points</div>
          <div style={{ background: "rgba(255,159,10,0.07)", border: "1px solid rgba(255,159,10,0.25)", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 5 }}>
            {ed.hooks.map((h, i) => <div key={i} style={{ fontSize: 12, color: "#f5c15a", display: "flex", gap: 7 }}><span>•</span><span>{h}</span></div>)}
          </div>
        </div>
      )}
    </div>
  );
}

function FullEnrichInsights({ lead, running }) {
  const f = (lead.enrich_data || {}).full;
  if (!f && !running) return null;
  const warn = "#ff9f0a", bad = "#ff453a";
  const breach = f && f.breach;
  const exp = f && f.exposure;
  const subs = (f && f.subdomains) || [];
  return (
    <div>
      <div style={sectionTitle}>Full enrich{f && f.at ? <span style={{ textTransform: "none", letterSpacing: 0, color: "#86868b", fontWeight: 400 }}> · {fmtUpdated(f.at)}</span> : null}</div>
      <div className="surface-dark" style={{ background: "#1c1c1e", border: "1px solid #2c2c2e", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 6 }}>
        {running && <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#64d2ff" }}><span className="spinner" /> Gathering breach exposure, subdomains and internet-facing services…</div>}
        {breach && (breach.employees || breach.users) ? (
          <InsightRow label="Breach exposure" tone={bad}>
            {breach.employees || 0} staff and {breach.users || 0} customer credential{(breach.users === 1) ? "" : "s"} seen in infostealer data{breach.third_parties ? ` · ${breach.third_parties} via third parties` : ""}
          </InsightRow>
        ) : (f && breach !== undefined && <InsightRow label="Breach exposure" tone="#30d158">None found</InsightRow>)}
        {subs.length > 0 && (
          <InsightRow label={`Subdomains (${subs.length})`}>{subs.slice(0, 12).join(", ")}{subs.length > 12 ? "…" : ""}</InsightRow>
        )}
        {exp && exp.skipped && <InsightRow label="Internet exposure" tone="#86868b">{exp.skipped}</InsightRow>}
        {exp && !exp.skipped && (exp.ports || []).length > 0 && (
          <InsightRow label="Internet exposure" tone={warn}>
            Server {exp.ip}{exp.org ? ` (${exp.org})` : ""} — open: {exp.ports.join(", ")}{exp.source === "InternetDB" ? " · via InternetDB" : ""}
          </InsightRow>
        )}
        {exp && !exp.skipped && (exp.vulns || []).length > 0 && (
          <InsightRow label={`Known CVEs (${exp.vulns.length})`} tone={bad}>{exp.vulns.slice(0, 8).join(", ")}{exp.vulns.length > 8 ? "…" : ""}</InsightRow>
        )}
        {exp && !exp.skipped && (exp.ports || []).length === 0 && <InsightRow label="Internet exposure" tone="#30d158">No services found on {exp.ip}{exp.source === "InternetDB" ? " · via InternetDB" : ""}</InsightRow>}
      </div>
    </div>
  );
}

// ── Background enrichment (v1.6) ──────────────────────────────────────────────

function EnrichBatch({ onFinished }) {
  const isMobile = useIsMobile();
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");

  const poll = useCallback(() => api("/api/sales/enrich-batch").then(d => { setState(d); return d; }).catch(() => null), []);

  useEffect(() => { poll(); }, [poll]);
  useEffect(() => {
    if (!state?.running) return;
    const id = setInterval(() => poll().then(d => { if (d && !d.running) onFinished && onFinished(); }), 4000);
    return () => clearInterval(id);
  }, [state?.running, poll, onFinished]);

  async function start(scope) {
    setOpen(false); setErr("");
    try { setState(await api("/api/sales/enrich-batch", { method: "POST", body: { scope } })); }
    catch (e) { setErr(e.message); }
  }

  if (state?.running) {
    const pct = state.total ? Math.round((100 * state.done) / state.total) : 0;
    return (
      <div style={{ ...btnSecondary, display: "flex", alignItems: "center", gap: 8, cursor: "default", minWidth: 220 }} title={state.current ? `Now: ${state.current}` : ""}>
        <span className="spinner" />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12 }}>Enriching {state.done} of {state.total}</div>
          <div style={{ height: 3, background: "#2c2c2e", borderRadius: 2, marginTop: 3 }}>
            <div style={{ width: `${pct}%`, height: 3, background: "#00d4ff", borderRadius: 2 }} />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={{ position: "relative" }}>
      <button className="btn btn-secondary" onClick={() => setOpen(o => !o)} title={state?.finished ? `Last run ${fmtUpdated(state.finished)}: ${state.done} leads` : ""}>Enrich ▾</button>
      {open && (
        <div className="surface-dark" style={{ position: "absolute", ...(isMobile ? { left: 0 } : { right: 0 }), top: "calc(100% + 6px)", zIndex: 30, background: "#1c1c1e", border: "1px solid #3a3a3c", borderRadius: 10, padding: 6, minWidth: 260, maxWidth: "calc(100vw - 32px)", boxShadow: "0 8px 30px rgba(0,0,0,0.4)" }}>
          {[["new", "All New leads"], ["unenriched", "All leads not yet enriched"], ["stale", "Enriched more than 30 days ago"]].map(([k, l]) => (
            <button key={k} className="menu-item" onClick={() => start(k)}>{l}</button>
          ))}
          <div style={{ fontSize: 11, color: "#86868b", padding: "6px 10px 4px", lineHeight: 1.5 }}>
            Runs in the background, about 5 seconds a lead. Each lead uses one web search credit the first time.
          </div>
        </div>
      )}
      {err && <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: "#3a1a1a", color: "#ff453a", fontSize: 12, padding: "6px 10px", borderRadius: 8, whiteSpace: "nowrap" }}>{err}</div>}
    </div>
  );
}

function ConvertModal({ lead, onClose, onDone }) {
  const primary = (lead.contacts || []).find(c => c.email && !c.do_not_contact) || {};
  const [f, setF] = useState({
    name: lead.company_name, domains: lead.domain || "", contact: primary.name || "",
    contact_email: primary.email || "", phone: primary.phone || "", company_code: "", notes: "",
  });
  const [carried, setCarried] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api(`/api/sales/leads/${lead.id}/convert-preview`).then(p => {
      setF(x => ({
        ...x,
        domains: x.domains || p.domains || "",
        contact: x.contact || p.contact || "",
        contact_email: x.contact_email || p.contact_email || "",
        phone: x.phone || p.phone || "",
        company_code: p.company_code || "",
      }));
      setCarried(p.carried || []);
    }).catch(() => setCarried([]));
  }, [lead.id]);

  async function go() {
    setBusy(true); setErr("");
    try { onDone(await api(`/api/sales/leads/${lead.id}/convert`, { method: "POST", body: { ...f, company_code: f.company_code.trim().toUpperCase() } })); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }
  const field = (k, label, props = {}) => (
    <div>
      <label style={labelStyle}>{label}</label>
      <input value={f[k]} onChange={e => setF(x => ({ ...x, [k]: e.target.value }))} style={inputStyle} {...props} />
    </div>
  );
  const codeOk = !f.company_code || /^[A-Za-z]{3}$/.test(f.company_code.trim());
  return (
    <Modal title="Add to Client Management" onClose={onClose} footer={<>
      {/* eslint-disable-next-line react/jsx-key */}
      <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary" disabled={busy || !f.name.trim() || !codeOk} onClick={go}>{busy ? "Creating…" : "Create client"}</button>
    </>}>
      <ErrorBox>{err}</ErrorBox>
      {field("name", "Client name", { maxLength: 80 })}
      {field("domains", "Domains", { placeholder: "comma separated" })}
      {lead.domain_status === "possible" && <div style={{ fontSize: 11, color: "#ff9f0a", marginTop: -8 }}>The domain is a possible match. Check it before saving.</div>}
      {field("contact", "Main contact")}
      {field("contact_email", "Contact email", { type: "email" })}
      {field("phone", "Phone")}
      <div>
        <label style={labelStyle}>Company code</label>
        <input value={f.company_code} maxLength={3} onChange={e => setF(x => ({ ...x, company_code: e.target.value.replace(/[^A-Za-z]/g, "").toUpperCase() }))}
          placeholder={carried === null ? "Working one out…" : "Picked automatically if left blank"} style={{ ...inputStyle, fontFamily: "var(--font-mono)", letterSpacing: "0.1em", width: 110 }} />
        <div style={{ fontSize: 11, color: codeOk ? "#86868b" : "#ff9f0a", marginTop: 4 }}>
          {codeOk ? "Suggested from the name and not used by any other client. You can change it." : "Must be three letters."}
        </div>
      </div>
      <div style={{ fontSize: 12, color: "#86868b", lineHeight: 1.6 }}>
        {carried === null ? "Checking what comes across…" : (
          <>
            Also comes across:
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {carried.map(c => <li key={c}>{c}</li>)}
              <li>A dated note saying the client came from the pipeline</li>
            </ul>
            The lead is marked Won.
          </>
        )}
      </div>
    </Modal>
  );
}

function AddLeadModal({ meta, onClose, onAdded }) {
  const [f, setF] = useState({ company_name: "", company_number: "", domain: "", postcode: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true); setErr("");
    try {
      const r = await api("/api/sales/leads", { method: "POST", body: { ...f, source: "manual" } });
      onAdded((r.ids || [])[0]);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }
  return (
    <Modal title="Add lead" onClose={onClose} footer={<>
      {/* eslint-disable-next-line react/jsx-key */}
      <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary" disabled={busy || !f.company_name.trim()} onClick={go}>{busy ? "Adding…" : "Add lead"}</button>
    </>}>
      <ErrorBox>{err}</ErrorBox>
      <div><label style={labelStyle}>Company name</label><input autoFocus value={f.company_name} onChange={e => setF(x => ({ ...x, company_name: e.target.value }))} style={inputStyle} /></div>
      <div><label style={labelStyle}>Companies House number (optional)</label><input value={f.company_number} onChange={e => setF(x => ({ ...x, company_number: e.target.value.toUpperCase().replace(/\s/g, "") }))} style={inputStyle} maxLength={8} /></div>
      <div><label style={labelStyle}>Domain (optional)</label><input value={f.domain} onChange={e => setF(x => ({ ...x, domain: e.target.value }))} placeholder="example.co.uk" style={inputStyle} /></div>
      <div><label style={labelStyle}>Postcode (optional)</label><input value={f.postcode} onChange={e => setF(x => ({ ...x, postcode: e.target.value.toUpperCase() }))} style={inputStyle} maxLength={10} /></div>
      <div style={{ fontSize: 12, color: "#86868b" }}>With a company number, Enrich fills in the sectors and directors from Companies House.</div>
    </Modal>
  );
}

function SuppressionModal({ onClose }) {
  const [rows, setRows] = useState([]);
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { api("/api/sales/suppression").then(setRows).catch(e => setErr(e.message)); }, []);
  async function add() {
    setErr("");
    try { setRows(await api("/api/sales/suppression", { method: "POST", body: { value, reason } })); setValue(""); setReason(""); }
    catch (e) { setErr(e.message); }
  }
  async function remove(r) {
    if (!window.confirm(`Remove ${r.value} from the suppression list? Outreach to it will be allowed again.`)) return;
    try { setRows(await api(`/api/sales/suppression/${r.id}`, { method: "DELETE" })); }
    catch (e) { setErr(e.message); }
  }
  return (
    <Modal title="Suppression list" onClose={onClose} maxWidth={560}>
      <div style={{ fontSize: 12, color: "#86868b", lineHeight: 1.6 }}>Addresses and domains here are never sent outreach email. Unsubscribe links add to this list automatically.</div>
      <ErrorBox>{err}</ErrorBox>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input value={value} onChange={e => setValue(e.target.value)} placeholder="name@company.co.uk or company.co.uk" style={{ ...inputStyle, flex: "2 1 200px", width: "auto", minWidth: 0 }} />
        <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (optional)" style={{ ...inputStyle, flex: "1 1 140px", width: "auto", minWidth: 0 }} />
        <button className="btn btn-primary" disabled={!value.trim()} onClick={add}>Add</button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.length === 0 && <div style={{ fontSize: 12, color: "#86868b" }}>Nothing suppressed yet.</div>}
        {rows.map(r => (
          <div className="surface-dark" key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, background: "#141414", border: "1px solid #2c2c2e", borderRadius: 8, padding: "8px 10px", minWidth: 0 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, color: "#f5f5f7", overflowWrap: "anywhere" }}>{r.value} <span style={{ fontSize: 11, color: "#86868b" }}>{r.kind}</span></div>
              <div style={{ fontSize: 11, color: "#86868b" }}>{r.reason ? `${r.reason} · ` : ""}Updated {fmtUpdated(r.created_at)}</div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => remove(r)}>Remove</button>
          </div>
        ))}
      </div>
    </Modal>
  );
}
